/**
 * Procurement API client — repointed to the rebuilt `/api` surface
 * (#1151 PR-4). The archived `/admin/procurement*` face is gone; the
 * single `/api` surface splits the domain into two resources under
 * `/api/purchase-suggestions` and `/api/purchase-orders`. Contract
 * mirrored from `backend/openapi.json`:
 *
 *   GET  /api/purchase-suggestions?status=&supplier_id=&page=&size=
 *          → { items,total,page,size }
 *   POST /api/purchase-suggestions              → PurchaseSuggestion
 *   POST /api/purchase-suggestions/:id/accept   → PurchaseOrder
 *   POST /api/purchase-suggestions/:id/dismiss  → PurchaseSuggestion
 *   GET  /api/purchase-orders?page=&size=&<field>=<op>:<value>
 *          → { items,total,page,size }  (shared list-query kernel, #1420:
 *          status eq/in, supplier_id eq/like)
 *   POST /api/purchase-orders                   → PurchaseOrder
 *   GET  /api/purchase-orders/:id               → { purchase_order, items }
 *   POST /api/purchase-orders/:id/submit        → PurchaseOrder
 *   POST /api/purchase-orders/:id/cancel        → PurchaseOrder
 *   POST /api/purchase-orders/:id/close         → PurchaseOrder
 *   POST /api/purchase-orders/:id/receive       → PurchaseOrder
 *
 * Envelope: every 2xx body is `{ data: T }` (ADR-0006 §5); `http`
 * unwraps the outer `data`, so these methods resolve the inner `T`
 * directly. Wire types come from the generated OpenAPI schema so a
 * backend contract change surfaces at typecheck.
 *
 * Decimal-string discipline (ADR-0003):
 *   `qty_ordered` / `qty_received` / `unit_price` / `total_amount` /
 *   `suggested_qty` are NUMERIC(12,3)-as-text on the wire — they
 *   round-trip as strings on both read and write (the backend zod
 *   schema validates a `^-?\d+(\.\d+)?$` string). UI render parses via
 *   `parseDecimalText` ONLY at the display boundary.
 */
import { http } from '@lib/api';
import type { ListPage } from './list-envelope';
import { toApiListParams } from './list-request';
import { PURCHASE_ORDER_FILTERS } from './filters.generated';
import type { components, paths } from '@/types/api.generated';

// The `/api/purchase-orders` filter whitelist (codegen mirror of the backend
// `purchaseOrderListConfig.filterable`). Re-exported so the resource definition
// pins `filterable` to the SAME const the request builder whitelists against.
export { PURCHASE_ORDER_FILTERS };

// ─────────────────────────────────────────────────────────────────
// Wire types — sourced from the generated OpenAPI schema.
// ─────────────────────────────────────────────────────────────────

export type PurchaseSuggestion = components['schemas']['PurchaseSuggestion'];
export type PurchaseOrder = components['schemas']['PurchaseOrder'];
export type PurchaseOrderItem = components['schemas']['PurchaseOrderItem'];
/** Detail read shape after `http` unwraps the `{ data }` envelope. */
export type PurchaseOrderDetail = components['schemas']['PurchaseOrderDetail']['data'];
export type PurchaseSuggestionStatus = PurchaseSuggestion['status'];
export type PurchaseOrderStatus = PurchaseOrder['status'];

/**
 * The measurement-unit closed set (#2058). Sourced from the generated request
 * bodies rather than the response schemas: the backend tightened only the
 * INPUT faces to `closedEnumText(CANONICAL_UNITS)`, so the generated union
 * lives on `requestBody` while the read shapes stay `string`.
 */
export type ProcurementUnit = NonNullable<
  paths['/api/purchase-suggestions']['post']['requestBody']
>['content']['application/json']['unit'];

export interface SuggestionListParams {
  status?: PurchaseSuggestionStatus;
  supplier_id?: string;
  page?: number;
  size?: number;
}

/**
 * PO list request shape (#1420). The list route rides the shared list-query
 * kernel: `page`/`size` are the fixed params and `extraParams` carries the
 * operator's serialized `<field>=<op>:<value>` filters (status / supplier_id),
 * whitelisted against `PURCHASE_ORDER_FILTERS` before they hit the wire.
 */
export interface PurchaseOrderListParams {
  page?: number;
  size?: number;
  /**
   * Kernel `<field>:<dir>` sort, whitelisted backend-side against
   * `purchaseOrderListConfig.sortable` (`PURCHASE_ORDER_SORTS` on this side),
   * which is what gates the clickable table headers; an out-of-set field 400s.
   * Omit to get the kernel default (`created_at:desc`).
   */
  sort?: string;
  extraParams?: Record<string, string>;
}

/** Manual suggestion entry — decimal-text `suggested_qty`. */
export interface CreateSuggestionInput {
  ingredient_id: string;
  supplier_id: string;
  suggested_qty: string;
  unit: ProcurementUnit;
  reason?: string;
}

/** Accept a suggestion → creates a draft PO. `unit_price` is the only
 *  required field; decimal-text per ADR-0003. */
export interface AcceptSuggestionInput {
  unit_price: string;
  currency?: string;
  expected_delivery_at?: string;
}

/** Create a PO directly. `qty_ordered` / `unit_price` are decimal-text. */
export interface CreatePurchaseOrderInput {
  supplier_id: string;
  currency?: string;
  expected_delivery_at?: string;
  notes?: string;
  items: Array<{
    ingredient_id: string;
    qty_ordered: string;
    unit: ProcurementUnit;
    unit_price: string;
  }>;
}

/** Receipt entry — one line per PO item. `qty` is the delivered amount
 *  for this receipt as decimal-text; the backend accumulates it against
 *  `qty_received` and rejects overflow past `qty_ordered`. */
export interface RecordReceiptInput {
  receipts: Array<{ item_id: string; qty: string }>;
}

/**
 * Parse a NUMERIC(12,3)-as-text value coming off the wire (ADR-0003).
 *
 * Why this exists: the backend stores `qty_*` / `unit_price` /
 * `total_amount` / `suggested_qty` as TEXT to avoid float drift. The
 * frontend cannot import the backend SSOT parser across the worker
 * boundary, so we mirror the parse leg here with the same fail-loud
 * contract (EQD §3 + class-wide "no bare Number() on Decimal-string
 * columns" rule that bit #628 before its hotfix). All procurement UI
 * render paths funnel through this helper rather than calling
 * `Number(...)` directly, so a future tighter contract only edits one
 * place.
 *
 * Bounded-input note: NUMERIC(12,3) holds 12 total digits with up to 3
 * after the decimal, so the largest magnitude is 999_999_999.999, well
 * inside IEEE-754 exact-decimal precision (≈15 sig digits). The helper
 * returns a plain `number` because every consumer here is a display
 * formatter or a `<` / `>` comparison, both exact at this magnitude.
 *
 * Throws on: empty string, non-numeric, NaN, infinity.
 */
export function parseDecimalText(raw: string, source: string): number {
  if (typeof raw !== 'string' || raw.length === 0) {
    throw new Error(`parseDecimalText: empty / non-string value at ${source}`);
  }
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    throw new Error(`parseDecimalText: non-finite value ${JSON.stringify(raw)} at ${source}`);
  }
  return n;
}

/**
 * The wire format contract for NUMERIC(12,3)-as-text write values
 * (ADR-0003 §Decimal). Kept in lockstep with the backend's zod guard
 * (`backend/src/contexts/supply/http/procurement.ts` /
 * `platform/db/shared-fields.ts`, both `/^-?\d+(\.\d+)?$/`). The
 * backend rejects the whole `receipts` batch atomically on the first
 * value that fails this regex, so the client must send only
 * contract-shaped strings.
 */
export const DECIMAL_TEXT_RE = /^-?\d+(\.\d+)?$/;

/**
 * Normalize free-text / keypad decimal input to the ADR-0003 wire
 * contract (`DECIMAL_TEXT_RE`), or return `null` when it cannot be.
 *
 * Why: the on-screen keypad and `<input type="number">` both emit lenient
 * shapes the backend regex rejects — a leading dot (`.5`), a trailing dot
 * (`5.`), surrounding whitespace, or exponent notation (`1e3`). Sending
 * any of those 400s the request (and, for receipts, fails the whole
 * batch). Callers normalize before send, or reject on `null`, so a bad
 * value never reaches the wire.
 *
 * Normalizes only shape, not sign/magnitude: `.5` → `0.5`, `-.5` → `-0.5`,
 * `5.` → `5`. Positivity / range remain the caller's business rule.
 */
export function normalizeDecimalText(raw: string): string | null {
  if (typeof raw !== 'string') return null;
  let s = raw.trim();
  // Require at least one digit so bare punctuation (`.`, `-`) can't be
  // fabricated into a valid `0` by the dot-fixups below.
  if (!/\d/.test(s)) return null;
  // Leading dot needs an integer part: `.5` → `0.5`, `-.5` → `-0.5`.
  if (s.startsWith('.')) s = `0${s}`;
  else if (s.startsWith('-.')) s = `-0${s.slice(1)}`;
  // Trailing dot is not part of the contract: `5.` → `5`.
  if (s.endsWith('.')) s = s.slice(0, -1);
  return DECIMAL_TEXT_RE.test(s) ? s : null;
}

export const procurementAPI = {
  // ── suggestions ──────────────────────────────────────────────
  listSuggestions: (params: SuggestionListParams = {}): Promise<ListPage<PurchaseSuggestion>> =>
    http.get<ListPage<PurchaseSuggestion>>('/api/purchase-suggestions', {
      params,
    }),

  createSuggestion: (input: CreateSuggestionInput): Promise<PurchaseSuggestion> =>
    http.post<PurchaseSuggestion>('/api/purchase-suggestions', input),

  acceptSuggestion: (id: string, input: AcceptSuggestionInput): Promise<PurchaseOrder> =>
    http.post<PurchaseOrder>(`/api/purchase-suggestions/${encodeURIComponent(id)}/accept`, input),

  dismissSuggestion: (id: string): Promise<PurchaseSuggestion> =>
    http.post<PurchaseSuggestion>(
      `/api/purchase-suggestions/${encodeURIComponent(id)}/dismiss`,
      {},
    ),

  // ── purchase orders ──────────────────────────────────────────
  listPurchaseOrders: (params: PurchaseOrderListParams = {}): Promise<ListPage<PurchaseOrder>> =>
    http.get<ListPage<PurchaseOrder>>('/api/purchase-orders', {
      // Bridge table state → kernel query: `limit`→`size`, and drop any filter
      // the backend does not accept rather than 400ing the whole page (#1154).
      params: toApiListParams(
        {
          page: params.page,
          limit: params.size,
          sort: params.sort,
          extraParams: params.extraParams,
        },
        PURCHASE_ORDER_FILTERS,
      ),
    }),

  getPurchaseOrder: (id: string): Promise<PurchaseOrderDetail> =>
    http.get<PurchaseOrderDetail>(`/api/purchase-orders/${encodeURIComponent(id)}`),

  createPurchaseOrder: (input: CreatePurchaseOrderInput): Promise<PurchaseOrder> =>
    http.post<PurchaseOrder>('/api/purchase-orders', input),

  submitPurchaseOrder: (id: string): Promise<PurchaseOrder> =>
    http.post<PurchaseOrder>(`/api/purchase-orders/${encodeURIComponent(id)}/submit`, {}),

  cancelPurchaseOrder: (id: string): Promise<PurchaseOrder> =>
    http.post<PurchaseOrder>(`/api/purchase-orders/${encodeURIComponent(id)}/cancel`, {}),

  closePurchaseOrder: (id: string): Promise<PurchaseOrder> =>
    http.post<PurchaseOrder>(`/api/purchase-orders/${encodeURIComponent(id)}/close`, {}),

  recordReceipt: (id: string, input: RecordReceiptInput): Promise<PurchaseOrder> =>
    http.post<PurchaseOrder>(`/api/purchase-orders/${encodeURIComponent(id)}/receive`, input),
};
